import { compact, get } from 'lodash';
import React, {
  MutableRefObject,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import classNames from 'classnames';
import { useClickOutside, useDeepCompareMemo, useEffectOnce, usePrevious, useUpdatedRef } from '../../hooks';
import PopoverRoot from './PopoverRoot';
import { getFallbackPlacements, sameWidthModifier } from './utils';
import { CreateComponentFunction, Placement, Trigger } from './index';
import { usePortalContext } from '../PortalContext/PortalContext';

export type Coordinates = { x: number; y: number };

export type PopoverProps = {
  className?: string;
  content: ReactNode | CreateComponentFunction;
  children: ReactNode;
  trigger?: Trigger;
  // possibility to combine trigger = 'hover' with close-on-click functionality
  closeTrigger?: 'click' | 'hover';
  closeOnClickOutside?: boolean;
  stopPropagation?: boolean;
  initialIsOpen?: boolean;
  animate?: boolean;
  CUSTOM_ATTRIBUTE_VALUETransitionDuration?: number;
  placement?: Placement;
  padding?: Partial<Coordinates>;
  onOpen?: () => void;
  onClose?: () => void;
  // updates with actual Popper placement, which can be different because of overflow, fallback placement, etc
  onPlacementChange?: (placement: Placement) => void;
  referenceContainerRef?: MutableRefObject<HTMLDivElement | null>;
  isInline?: boolean;
  isFullWidth?: boolean;
  isMatchReferenceWidth?: boolean;
  noUnmount?: boolean;
  IsFallbackPlacementEnabled?: boolean;
  onPopoverMouseOver?: () => void;
  onPopoverMouseOut?: () => void;
  CUSTOM_ATTRIBUTE_VALUEContainerReferenceRef?: MutableRefObject<HTMLDivElement | null>;
  isClickOutsideDisabled?: boolean;
  delayOnHover?: number;
  isDisabled?: boolean;
  isDynamic?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  useProvidedRoot?: boolean;
};

export type PopoverRef = {
  open: () => void;
  close: () => void;
};

export const Popover = forwardRef<PopoverRef, PopoverProps>((props, ref) => {
  const { portalRoot } = usePortalContext();
  const {
    className,
    children,
    content,
    trigger = 'hover',
    closeTrigger,
    closeOnClickOutside = false,
    stopPropagation = true,
    animate,
    onOpen,
    onClose,
    padding,
    initialIsOpen = false,
    placement = 'bottom',
    referenceContainerRef,
    isInline = false,
    isFullWidth = false,
    isMatchReferenceWidth = false,
    noUnmount = false,
    IsFallbackPlacementEnabled = true,
    CUSTOM_ATTRIBUTE_VALUEContainerReferenceRef,
    onPopoverMouseOver,
    onPopoverMouseOut,
    delayOnHover = 100,
    isDynamic = false,
    isDisabled = false,
    onClick,
    onPlacementChange,
    useProvidedRoot,
  } = props;

  const [isReferenceHovering, setIsReferenceHovering] = useState(false);
  const [isPopoverHovering, setIsPopoverHovering] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const popoverElementRef = useRef<HTMLDivElement | null>(null);
  const onOpenRef = useUpdatedRef(onOpen);
  const onCloseRef = useUpdatedRef(onClose);
  const [shouldUnmount, setShouldUnmount] = useState(true);
  const isHovering = isReferenceHovering || isPopoverHovering;

  const handlePopoverElement = useCallback((node: HTMLDivElement | null) => {
    popoverElementRef.current = node;
    setPopperElement(node);
  }, []);

  const handleReferenceElement = useCallback(
    (node: HTMLDivElement | null) => {
      if (CUSTOM_ATTRIBUTE_VALUEContainerReferenceRef) {
        setReferenceElement(CUSTOM_ATTRIBUTE_VALUEContainerReferenceRef.current);
        return;
      }
      if (referenceContainerRef) {
        referenceContainerRef.current = node;
      }
      setReferenceElement(node);
    },
    [CUSTOM_ATTRIBUTE_VALUEContainerReferenceRef, referenceContainerRef],
  );

  const refElementCoordinates = isDynamic ? referenceElement?.getBoundingClientRect() : { x: 0 };

  const {
    styles: popperStyles,
    attributes: popperAttributes,
    update,
    state: popperState,
  } = usePopper(
    referenceElement,
    popperElement,
    // the hook needs stable data
    useDeepCompareMemo(
      () => {
        const { x, y }: Coordinates = {
          // Math.random() also will result in 0, but will ensure the popover change its position on mouse move
          x: isDynamic ? Math.random() : 0,
          y: 4,
          ...padding,
        };
        const fallbackPlacements = IsFallbackPlacementEnabled ? getFallbackPlacements(placement) : [];
        return {
          strategy: 'absolute',
          placement,
          modifiers: compact([
            {
              name: 'offset',
              options: {
                offset: [x, y],
              },
            },
            isMatchReferenceWidth ? sameWidthModifier : undefined,
            fallbackPlacements
              ? {
                  name: 'flip',
                  options: {
                    fallbackPlacements,
                  },
                }
              : undefined,
          ]),
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [IsFallbackPlacementEnabled, isMatchReferenceWidth, padding, placement, refElementCoordinates?.x],
    ),
  );

  const previousPlacement = usePrevious(popperState?.placement);

  useEffect(() => {
    if (popperState?.placement && previousPlacement !== popperState.placement) {
      onPlacementChange?.(popperState.placement);
    }
  }, [onPlacementChange, popperState?.placement, previousPlacement]);

  // https://popper.js.org/docs/v2/modifiers/hide/
  // popperAttributes type is not correct
  const isReferenceHidden = get(popperAttributes as unknown, 'popper.data-popper-reference-hidden') === true;

  const open = useCallback(() => {
    setIsOpen(true);
    onOpenRef.current?.();
  }, [onOpenRef]);

  const close = useCallback(() => {
    setIsPopoverHovering(false);
    setIsReferenceHovering(false);
    setIsOpen(false);
    onCloseRef.current?.();
  }, [onCloseRef]);

  useClickOutside(
    popoverElementRef,
    () => {
      if (isOpen) {
        close();
      }
    },
    closeOnClickOutside,
  );

  const onSizeChange = useCallback(() => {
    if (update) {
      void update();
    }
  }, [update]);

  useImperativeHandle(
    ref,
    () => {
      return {
        open,
        close,
      };
    },
    [open, close],
  );

  useEffect(() => {
    if (trigger === 'hover') {
      const timeoutId = setTimeout(
        () => {
          if (isHovering) {
            open();
          } else {
            close();
          }
        },
        // we add 100 msec to let the user the ability to move the popover itself without closing
        isHovering ? delayOnHover : 100,
      );

      return () => {
        clearTimeout(timeoutId);
      };
    }

    if (closeTrigger === 'hover' && !isHovering) {
      const timeoutId = setTimeout(
        () => {
          if (!isHovering) {
            close();
          }
        },
        // we add 100 msec to let the user the ability to move the popover itself without closing
        isHovering ? delayOnHover : 100,
      );

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isHovering, trigger, open, close, delayOnHover, closeTrigger]);

  useEffectOnce(() => {
    setShouldUnmount(!noUnmount);

    if (initialIsOpen) {
      open();
    }
  });

  useEffect(() => {
    if (isReferenceHidden) {
      close();
    }
  }, [isReferenceHidden, close]);

  return (
    <>
      <div
        className={classNames(className, 'ui-popover__reference-value')}
        onMouseEnter={() => {
          if (isDisabled) {
            return;
          }

          setIsReferenceHovering(true);
        }}
        onMouseLeave={() => {
          if (isDisabled) {
            return;
          }
          setIsReferenceHovering(false);
        }}
        onMouseDown={() => {
          if (isDisabled) {
            return;
          }

          if (closeTrigger === 'click' && isOpen) {
            close();
          }

          if (trigger === 'click') {
            if (isOpen) {
              close();
            } else {
              open();
            }
          }
        }}
        onClick={(event) => {
          if (isDisabled) {
            return;
          }

          onClick?.(event);

          if (trigger === 'click' && stopPropagation) {
            event.stopPropagation();
          }
        }}
        ref={handleReferenceElement}
        style={{ display: isInline ? 'inline-flex' : undefined }}
      >
        {children}
      </div>
      {((isOpen && !isDisabled) || !shouldUnmount) &&
        createPortal(
          <div
            className={'ui-tooltip-popover'}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onMouseEnter={() => {
              setIsPopoverHovering(true);
              onPopoverMouseOver?.();
            }}
            onMouseLeave={() => {
              setIsPopoverHovering(false);
              onPopoverMouseOut?.();
            }}
            ref={handlePopoverElement}
            style={{
              ...popperStyles.popper,
              width: isFullWidth ? '100%' : popperStyles.popper.width,
              display: !isOpen && noUnmount ? 'none' : popperStyles.popper.display,
              // make sure to show the popover only when position is set
              visibility: popperState === null ? 'hidden' : undefined,
              zIndex: 'var(--z-index--popover)',
            }}
            {...popperAttributes.popper}
          >
            <PopoverRoot animate={animate} onSizeChange={onSizeChange}>
              {typeof content === 'function' ? content(close) : content}
            </PopoverRoot>
          </div>,
          (useProvidedRoot ? portalRoot : undefined) ?? (document.getElementById('root') as HTMLElement),
        )}
    </>
  );
});

export const usePopoverRef = () => {
  return useRef<null | PopoverRef>(null);
};
